import type { CSSProperties, ReactElement, RefObject } from 'react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

/*!**********************************************************************************************************************
 * Temporary fix for the issue with the useSelectionContainer hook
 * PR made to original project, but for now I think vendoring like this is fine.
 * Original source:
 * * https://github.com/AirLabsTeam/react-drag-to-select/blob/main/src/hooks/useSelectionLogic.ts
 * * @air/react-drag-to-select v5.0.8
 *
 * PR:
 * * https://github.com/AirLabsTeam/react-drag-to-select/pull/46
 */

interface SelectionContainerProps extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * This is a component responsible for displaying mouse selection box
 */
const SelectionContainer = forwardRef(({ style = {}, ...props }: SelectionContainerProps, ref) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectionBoxRef = useRef<HTMLDivElement>(null);
  const [, setForceUpdate] = useState(0);

  useImperativeHandle(
    ref,
    (): SelectionContainerRef => ({
      getBoundingClientRect: () => containerRef.current?.getBoundingClientRect(),
      getParentBoundingClientRect: () =>
        containerRef?.current?.parentElement?.getBoundingClientRect(),
      drawSelectionBox: (box: SelectionBox) => {
        requestAnimationFrame(() => {
          if (selectionBoxRef.current) {
            const styles: React.CSSProperties = {
              border: '1px solid #4C85D8',
              background: 'rgba(155, 193, 239, 0.4)',
              position: 'absolute',
              pointerEvents: 'none',
              ...style,
              top: box.top,
              left: box.left,
              width: box.width,
              height: box.height,
            };

            selectionBoxRef.current.style.cssText = styleObjectToCSS(styles);
          }
        });
      },
      clearSelectionBox: () => {
        requestAnimationFrame(() => {
          if (selectionBoxRef.current) {
            const styles: React.CSSProperties = {
              top: 0,
              left: 0,
              width: 0,
              height: 0,
            };

            selectionBoxRef.current.style.cssText = styleObjectToCSS(styles);
          }
        });
      },
    })
  );

  useEffect(() => {
    setForceUpdate(number => number + 1);
  }, []);

  return (
    <div ref={containerRef}>
      {containerRef.current
        ? createPortal(<div ref={selectionBoxRef} {...props} />, containerRef.current)
        : null}
    </div>
  );
});

interface UseSelectionContainerResult {
  /**
   * method to cancel current selecting
   */
  cancelCurrentSelection: ReturnType<typeof useSelectionLogic>['cancelCurrentSelection'];
  /**
   * ReactNode which displays mouse selection. It should be rendered at the top of container of elements we want to select
   */
  DragSelection: () => ReactElement;
}

interface UseSelectionContainerParams<T extends HTMLElement>
  extends Pick<
    UseSelectionLogicParams<T>,
    | 'onSelectionChange'
    | 'onSelectionEnd'
    | 'onSelectionStart'
    | 'isEnabled'
    | 'eventsElement'
    | 'shouldStartSelecting'
  > {
  /** These are props that get passed to the selection box component (where styling gets passed in) */
  selectionProps?: SelectionContainerProps;
}

/**
 * Use this hook to enable mouse selection on a container.
 * To prevent interfering with drag-n-drop feature, add data-draggable='true' to draggable item. Selection won't fire when click happens on that element
 */
export function useSelectionContainer<T extends HTMLElement>(
  props?: UseSelectionContainerParams<T>
): UseSelectionContainerResult {
  const {
    onSelectionChange,
    onSelectionEnd,
    onSelectionStart,
    isEnabled = true,
    selectionProps = {},
    eventsElement,
    shouldStartSelecting,
  } = props || {};

  const containerRef = useRef<SelectionContainerRef>(null);

  const { cancelCurrentSelection } = useSelectionLogic({
    containerRef,
    onSelectionEnd,
    onSelectionStart,
    onSelectionChange,
    isEnabled,
    eventsElement,
    shouldStartSelecting,
  });

  const DragSelection = useCallback(
    () => <SelectionContainer ref={containerRef} {...selectionProps} />,
    [selectionProps]
  );

  return {
    cancelCurrentSelection,
    DragSelection,
  };
}

interface UseSelectionLogicResult {
  cancelCurrentSelection: () => void;
}

interface UseSelectionLogicParams<T extends HTMLElement> {
  /** This callback will fire when the user starts selecting */
  onSelectionStart?: (event: MouseEvent) => void;
  /** This callback will fire when the user finishes selecting */
  onSelectionEnd?: (event: MouseEvent) => void;
  /** This callback will fire when the user's mouse changes position while selecting using requestAnimationFrame */
  onSelectionChange?: OnSelectionChange;
  /** This boolean enables selecting  */
  isEnabled?: boolean;
  /** This is an HTML element that the mouse events (mousedown, mouseup, mousemove) should be attached to. Defaults to the document.body */
  eventsElement?: T | null;
  /** This is the ref of the parent of the selection box  */
  containerRef: RefObject<SelectionContainerRef>;
  /**
   * If supplied, this callback is fired on mousedown and can be used to prevent selection from starting.
   * This is useful when you want to prevent certain areas of your application from being able to be selected.
   * Returning true will enable selection and returning false will prevent selection from starting.
   *
   * @param {EventTarget | null} target - The element the mousedown event fired on when the user started selected
   */
  shouldStartSelecting?: (target: EventTarget | null) => boolean;
}

/**
 * This hook contains logic for selecting. It starts 'selection' on mousedown event and finishes it on mouseup event.
 * When mousemove event is detected and user is selecting, it calls onSelectionChange and containerRef.drawSelectionBox
 */
export function useSelectionLogic<T extends HTMLElement>({
  containerRef,
  onSelectionChange,
  onSelectionStart,
  onSelectionEnd,
  isEnabled = true,
  eventsElement,
  shouldStartSelecting,
}: UseSelectionLogicParams<T>): UseSelectionLogicResult {
  const startPoint = useRef<null | Point>(null);
  const endPoint = useRef<null | Point>(null);
  const isSelecting = useRef(false);

  // these are used in listeners attached to eventsElement. They are used as refs to ensure we always use the latest version
  const currentSelectionChange = useRef(onSelectionChange);
  const currentSelectionStart = useRef(onSelectionStart);
  const currentSelectionEnd = useRef(onSelectionEnd);
  const onChangeRefId = useRef<number | undefined>();
  const isEnabledRef = useRef(isEnabled);

  currentSelectionChange.current = useCallback(
    (box: Box) => {
      onChangeRefId.current = onSelectionChange
        ? requestAnimationFrame(() => {
            onSelectionChange(box);
          })
        : undefined;
    },
    [onSelectionChange]
  );
  currentSelectionStart.current = onSelectionStart;
  currentSelectionEnd.current = onSelectionEnd;
  isEnabledRef.current = isEnabled;

  /**
   * Method to cancel selecting and reset internal data
   */
  const cancelCurrentSelection = useCallback(() => {
    startPoint.current = null;
    endPoint.current = null;
    isSelecting.current = false;
    containerRef.current?.clearSelectionBox();
    if (typeof onChangeRefId.current === 'number') {
      cancelAnimationFrame(onChangeRefId.current);
    }
  }, [containerRef]);

  /**
   * method to calculate point from event in context of the whole screen
   */
  const getPointFromEvent = useCallback(
    (event: MouseEvent, rect?: DOMRect): Point => {
      if (!rect) {
        rect = containerRef.current?.getParentBoundingClientRect();
      }

      return {
        x: event.clientX - (typeof rect?.left === 'number' ? rect.left : 0),
        y: event.clientY - (typeof rect?.top === 'number' ? rect.top : 0),
      };
    },
    [containerRef]
  );

  /**
   * Method called on mousemove event
   */
  const handleMouseMove = useCallback(
    (event: MouseEvent, rect?: DOMRect) => {
      if (startPoint.current && endPoint.current) {
        if (!rect) {
          return;
        }

        const newSelectionBox = calculateSelectionBox({
          startPoint: startPoint.current,
          endPoint: endPoint.current,
        });

        // calculate box in context of container to compare with items' coordinates
        const boxInContainer: SelectionBox = {
          ...newSelectionBox,
          top: newSelectionBox.top + (rect?.top || 0),
          left: newSelectionBox.left + (rect?.left || 0),
        };

        // we detect move only after some small movement
        if (calculateBoxCircumference(newSelectionBox) > 10) {
          if (!isSelecting.current) {
            if (currentSelectionStart?.current) {
              currentSelectionStart.current(event);
            }
            isSelecting.current = true;
          }
          containerRef.current?.drawSelectionBox(newSelectionBox);
          currentSelectionChange.current?.(boxInContainer);
        } else if (isSelecting.current) {
          currentSelectionChange.current?.(boxInContainer);
        }
      } else {
        cancelCurrentSelection();
      }
    },
    [cancelCurrentSelection, containerRef]
  );

  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!startPoint.current) {
        return;
      }

      const rect = containerRef.current?.getParentBoundingClientRect();
      endPoint.current = getPointFromEvent(event, rect);
      handleMouseMove(event, rect);
    },
    [handleMouseMove, getPointFromEvent, containerRef]
  );

  const onMouseUp = useCallback(
    (event: MouseEvent) => {
      /**
       * handle only left button up event
       */
      if (event.button === 0) {
        /**
         * If the user just clicked down and up in the same place without dragging,
         * we don't want to fire the onSelectionEnd event. We can do this
         * by checking if endPoint.current exists.
         */
        if (endPoint.current) {
          currentSelectionEnd.current?.(event);
        }

        cancelCurrentSelection();

        document.body.style.removeProperty('user-select');
        document.body.style.removeProperty('-webkit-user-select');

        (eventsElement || document.body).removeEventListener('mousemove', onMouseMove);
        window?.removeEventListener('mouseup', onMouseUp);
      }
    },
    [eventsElement, cancelCurrentSelection, onMouseMove]
  );

  const shouldStartSelectingRef = useRef(shouldStartSelecting);
  shouldStartSelectingRef.current = shouldStartSelecting;

  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      // handle only left button click
      if (e.button === 0 && isEnabledRef.current) {
        if (
          typeof shouldStartSelectingRef.current === 'function' &&
          !shouldStartSelectingRef.current(e.target)
        ) {
          return;
        }

        // disable text selection for all document
        document.body.style.userSelect = 'none';
        document.body.style.webkitUserSelect = 'none';
        startPoint.current = getPointFromEvent(e);

        (eventsElement || document.body).addEventListener('mousemove', onMouseMove);
        window?.addEventListener('mouseup', onMouseUp);
      }
    },
    [eventsElement, getPointFromEvent, onMouseMove, onMouseUp]
  );

  useEffect(() => {
    /**
     * On mount, add the mouse down listener to begin listening for dragging
     */
    (eventsElement || document.body).addEventListener('mousedown', onMouseDown);

    /**
     * On unmount, remove any listeners that we're applied.
     */
    return () => {
      (eventsElement || document.body).removeEventListener('mousedown', onMouseDown);
      (eventsElement || document.body).removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [eventsElement, onMouseDown, onMouseMove, onMouseUp]);

  return {
    cancelCurrentSelection,
  };
}

interface Point {
  x: number;
  y: number;
}

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface SelectionBox extends Box {}

type OnSelectionChange = (box: SelectionBox) => void;

interface SelectionContainerRef {
  drawSelectionBox: OnSelectionChange;
  clearSelectionBox: () => void;
  getBoundingClientRect: () => DOMRect | undefined;
  getParentBoundingClientRect: () => DOMRect | undefined;
}

const calculateSelectionBox = ({
  startPoint,
  endPoint,
}: {
  startPoint: Point;
  endPoint: Point;
}): Box => ({
  left: Math.min(startPoint.x, endPoint.x),
  top: Math.min(startPoint.y, endPoint.y),
  width: Math.abs(startPoint.x - endPoint.x),
  height: Math.abs(startPoint.y - endPoint.y),
});

const calculateBoxCircumference = (box: Box) => 2 * (box.width + box.height);

const isUnitlessNumber: Record<string, boolean> = {
  boxFlex: true,
  boxFlexGroup: true,
  columnCount: true,
  flex: true,
  flexGrow: true,
  flexPositive: true,
  flexShrink: true,
  flexNegative: true,
  fontWeight: true,
  lineClamp: true,
  lineHeight: true,
  opacity: true,
  order: true,
  orphans: true,
  widows: true,
  zIndex: true,
  zoom: true,

  // SVG-related properties
  fillOpacity: true,
  strokeDashoffset: true,
  strokeOpacity: true,
  strokeWidth: true,
};

function buildRule(key: string, value: unknown) {
  // Follows syntax at https://developer.mozilla.org/en-US/docs/Web/CSS/content,
  // including multiple space separated values.
  const unquotedContentValueRegex =
    /^(normal|none|(\b(url\([^)]*\)|chapter_counter|attr\([^)]*\)|(no-)?(open|close)-quote|inherit)((\b\s*)|$|\s+))+)$/;
  if (!isUnitlessNumber[key] && typeof value === 'number') {
    value = '' + value + 'px';
  } else if (
    key === 'content' &&
    typeof value === 'string' &&
    !unquotedContentValueRegex.test(value)
  ) {
    value = "'" + value.replace(/'/g, "\\'") + "'";
  }

  return hyphenateStyleName(key) + ': ' + value + ';  ';
}

function styleObjectToCSS(rules: CSSProperties) {
  const isArray = Array.isArray;
  const keys = Object.keys;
  let result = '';
  if (!rules || keys(rules).length === 0) {
    return result;
  }
  const styleKeys = keys(rules) as Array<keyof CSSProperties>;
  for (let j = 0, l = styleKeys.length; j < l; j++) {
    const styleKey = styleKeys[j];
    const value = rules[styleKey];

    if (isArray(value)) {
      for (let i = 0, len = value.length; i < len; i++) {
        result += buildRule(styleKey, value[i]);
      }
    } else {
      result += buildRule(styleKey, value);
    }
  }
  return result;
}

/**
 * Hyphenates a camelcased string, for example:
 *
 *   > hyphenate('backgroundColor')
 *   < "background-color"
 *
 * For CSS style names, use `hyphenateStyleName` instead which works properly
 * with all vendor prefixes, including `ms`.
 *
 * @param {string} string
 * @return {string}
 */
function hyphenate(string: string) {
  const _uppercasePattern = /([A-Z])/g;
  return string.replace(_uppercasePattern, '-$1').toLowerCase();
}

/**
 * Hyphenates a camelcased CSS property name, for example:
 *
 *   > hyphenateStyleName('backgroundColor')
 *   < "background-color"
 *   > hyphenateStyleName('MozTransition')
 *   < "-moz-transition"
 *   > hyphenateStyleName('msTransition')
 *   < "-ms-transition"
 *
 * As Modernizr suggests (http://modernizr.com/docs/#prefixed), an `ms` prefix
 * is converted to `-ms-`.
 *
 * @param {string} string
 * @return {string}
 */
function hyphenateStyleName(string: string) {
  const msPattern = /^ms-/;

  return hyphenate(string).replace(msPattern, '-ms-');
}
